import { useEffect, useRef } from "react";
import R from "ramda";
import dispatch from "../../../ccx-utils/js/tracking/adobe-dtm/events/dispatch";

/**
 * Helper function to safely get and lowercase a property
 * @param {string} propName - The name of the property to get and lowercase
 * @param {Object} obj - The object to get the property from
 * @returns {string} The lowercase value of the property, or an empty string if the property is not found
 */
const safeLowerCase = (propName, obj) => {
  const value = R.prop(propName, obj);
  return value ? R.toLower(value) : '';
};

/**
 * Transforms MikMak checkout data to match expected Adobe tracking structure
 * @param {Object} entry - The MikMak checkout event entry from dataLayer
 * @returns {Object} Transformed detail object for Adobe tracking
 */
const transformMikMakData = (entry) => ({
  eventInfo: {
    eventAction: "ca_buy_now",
    eventName: "buyNow",
    eventType: "userEvent",
    currencyCode: R.prop("product_price_currency", entry),
    detail: {
      name: safeLowerCase("product_name", entry),
      shade: safeLowerCase('product_model', entry),
      price: R.prop("product_price", entry),
      productCategory: safeLowerCase("product_category", entry),
      productSku: R.prop("product_id", entry),
      quantity: 1,
      retailer: R.prop("retailer_name", entry),
    },
  },
});

/**
 * Processes MikMak checkout events and dispatches them to Adobe tracking
 * @param {Object} entry - The MikMak checkout event entry from dataLayer
 */
const processMikMakCheckout = (entry) => {
  // Transform the data to match expected Adobe structure
  const transformedDetail = transformMikMakData(entry);

  dispatch({
    eventName: "buyNow",
    detail: transformedDetail,
  });
};

/**
 * Custom hook that monitors window.dataLayer for MikMak checkout events
 * and bridges them to Adobe DTM tracking
 *
 * This hook:
 * - Polls window.dataLayer every 200ms for new entries
 * - Filters for 'mikmak_checkout' events
 * - Dispatches matching events to Adobe tracking as 'buyNow' events
 *
 * @returns {void}
 */
export const useMikMakAdobeTracking = () => {
  const intervalRef = useRef(null);
  const lastIndexRef = useRef(0);

  useEffect(() => {
    if (!window || !window.dataLayer) {
      console.warn("window.dataLayer is not available");
      return;
    }

    // Initialize lastIndex to current dataLayer length
    lastIndexRef.current = window.dataLayer.length;

    // Set up interval to monitor dataLayer for new entries
    intervalRef.current = setInterval(() => {
      const newEntries = window.dataLayer.slice(lastIndexRef.current);

      newEntries.forEach((entry) => {
        if (entry && entry.event === "mikmak_checkout") {
          processMikMakCheckout(entry);
        }
      });

      lastIndexRef.current = window.dataLayer.length;
    }, 200); // Check every 200ms

    // Cleanup function
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);
};
